/* eslint-disable max-lines */
/* eslint-disable complexity */
import api from 'app/api';
import axios from 'app/api/base';
import { globalQueryClient } from 'app/configs/queryClientConfig';
import { getSharedRef } from 'app/hooks/sharedRefUtils';
import { getImageClassificationType } from 'app/routes/ManualReclassV2/components/LeftPane/util';
import { GLOBAL_CONSTS } from 'app/utils/constants';
import { DEFECT_REFETCH_DELAY_MS } from 'app/utils/defectRefetchDelay';
import keyBy from 'lodash/keyBy';
import queryString from 'query-string';
import store from 'store/index';
import {
  addUserClassification,
  changeGridMode,
  getDefect,
  getFileSetApiParamAnnotationType,
  getFileSetLocationParams,
  getUserClassification,
  handleUserClassificationChange,
  removeFileSet,
  resetFileSetLocalDefects,
  setActiveImg,
  setBulkFileSetLocalDefects,
  setContainerMeta,
  setFetchingReviewDataBackground,
  setFileSetLocalDefects,
  setFileSetLocalDefectsSavingStatus,
  setOtherDefects,
  setReviewData,
  setReviewDataNextApi,
  setReviewDefects,
  setSelectAll,
  setSelectedTool,
  updateFilsetTagsById,
  updateImageModes,
  updateTransferableImageIndexes,
  updateTransferImageIndexes,
  updateUserClassification,
} from 'store/reviewData/actions';
import {
  AUDIT,
  CLUSTERING,
  fileSetLocalDefectsStatus,
  MANUAL_CLASSIFY,
  SORTING_CONSTANTS,
} from 'store/reviewData/constants';

import { pipeFunc } from '.';
import {
  DEFECT_HOT_KEYS,
  REVIEW_CONTAINER_META_MAX_HEIGHT,
  TOOL_KEYS,
} from './constants';
import { getFileSetIds } from './fileSet';
import {
  encodeURL,
  formatFileSetData,
  getAIDefects,
  getDateFromParams,
  updateNextDataURL,
} from './helpers';
import { infoToast, warningToast } from './toast';

function calcContainerLayout({
  originalImgHeight,
  originalImgWidth,
  totalItemCount,
  totalWidth,
  maxHeight = 200,
}) {
  const imgAspectRatio = originalImgWidth / originalImgHeight;
  let rowAspectRatio = totalWidth / maxHeight;

  const gapBetweenImages = 16;
  const minWidth = 200;

  let maxPossibleImagesWidth;
  let imgWidth = 0;

  let imgCount = 0;

  if (Math.ceil(imgAspectRatio) >= Math.floor(rowAspectRatio)) {
    imgCount = 1;
  } else {
    if (imgAspectRatio < 0.5) {
      maxHeight = 800;
      rowAspectRatio = totalWidth / maxHeight;
    }

    while (imgWidth < minWidth) {
      if (imgWidth) {
        imgCount = 0;
        maxHeight += 20;
        rowAspectRatio = totalWidth / maxHeight;
      }

      let combinedAspectRatioOfImages = 0;

      while (combinedAspectRatioOfImages <= rowAspectRatio) {
        combinedAspectRatioOfImages += imgAspectRatio;
        imgCount += 1;
      }

      maxPossibleImagesWidth = totalWidth - gapBetweenImages * imgCount;
      imgWidth = maxPossibleImagesWidth / imgCount;
    }
  }

  if (!maxPossibleImagesWidth) {
    maxPossibleImagesWidth = totalWidth - gapBetweenImages * imgCount;
  }

  if (!imgWidth) {
    imgWidth = maxPossibleImagesWidth / imgCount;
  }

  const imgHeight = imgWidth / imgAspectRatio;

  const colCount = imgCount > totalItemCount ? totalItemCount : imgCount;
  const rowCount = Math.ceil(totalItemCount / colCount);
  const cardHeight = imgHeight + 35 + 16;

  return {
    rowCount,
    colCount,
    cardHeight,
    totalHeight: cardHeight * rowCount,
    cardWidth: imgWidth + 16,
  };
}

function handleCardSelect(index, isSelected) {
  const { getState, dispatch } = store;
  const { activeImg } = getState().review;
  if (isSelected) {
    dispatch(setActiveImg(activeImg.filter(x => x !== index)));
  } else {
    dispatch(setActiveImg([...activeImg, index]));
  }
}

function handleCardClick(event, index, isSelected, forceUpdate = false) {
  const { getState, dispatch } = store;
  const { activeImg, selectAll, data: fileSets } = getState().review;

  if (event.ctrlKey || event.metaKey) {
    handleCardSelect(index, isSelected);
  } else if (event.shiftKey) {
    const tempList = [];

    if (activeImg.sort()[activeImg.length - 1] < index) {
      for (
        let i = activeImg.sort()[activeImg.length - 1] + 1;
        i <= index;
        i++
      ) {
        tempList.push(i);
      }
    } else if (activeImg.length) {
      for (let i = index; !activeImg.includes(i); i++) {
        tempList.push(i);
      }
    }

    dispatch(setActiveImg([...new Set([...activeImg, ...tempList])]));
  } else {
    if (
      activeImg.length === 1 &&
      activeImg[0] === index &&
      !forceUpdate &&
      !selectAll
    ) {
      return;
    }

    const tempIndex = activeImg[0];

    dispatch(setActiveImg([index]));

    if (selectAll) {
      dispatch(setSelectAll(false));
    }

    if (fileSets[index]?.use_case !== fileSets[tempIndex]?.use_case) {
      dispatch(setOtherDefects([]));
      globalQueryClient.invalidateQueries('useCaseDefects');
    }

    // const { state } = location;
  }
}

function handleCardDoubleClick(index) {
  const { getState, dispatch } = store;
  const { activeGridMode } = getState().review;

  if (activeGridMode === 'Grid View') {
    dispatch(setActiveImg([index]));
    dispatch(changeGridMode('Canvas View'));
  }
}

function handleCardMultiSelect(index, direction) {
  const { getState, dispatch } = store;
  const { activeImg } = getState().review;

  if (activeImg.includes(index)) {
    if (activeImg.length > 1) {
      let updatedIndex = index;

      if (direction === 'down') {
        if (activeImg.includes(index - 1)) {
          updatedIndex = index - 1;
        }
      } else if (direction === 'up' && activeImg.includes(index + 1)) {
        updatedIndex = index + 1;
      }

      dispatch(setActiveImg(activeImg.filter(d => d !== updatedIndex)));
    }
  } else {
    dispatch(setActiveImg([...activeImg, index]));
  }
}

function handleImgClickNavigation(direction, event, forceUpdate = false) {
  const { getState, dispatch } = store;
  const { activeImg, selectAll, data: fileSets } = getState().review;

  let index;

  const currentActiveImage = event.shiftKey
    ? activeImg[activeImg.length - 1]
    : activeImg[0];

  if (direction === 'down') {
    index = currentActiveImage + 1;

    if (index > fileSets.length - 1) {
      return;
    }
  } else if (direction === 'up') {
    index = currentActiveImage - 1;

    if (index < 0) {
      return;
    }
  }

  if (event.shiftKey) {
    return handleCardMultiSelect(index, direction);
  }

  if (
    activeImg.length === 1 &&
    activeImg[0] === index &&
    !forceUpdate &&
    !selectAll
  ) {
    return;
  }

  const tempIndex = activeImg[0];

  dispatch(setActiveImg([index]));
  dispatch(setSelectAll(false));

  if (fileSets[index].use_case !== fileSets[tempIndex].use_case) {
    dispatch(setOtherDefects([]));
    globalQueryClient.invalidateQueries('useCaseDefects');
  }
}

function handleToggleSelectAll() {
  const { getState, dispatch } = store;
  const { selectAll } = getState().review;

  dispatch(setSelectAll(!selectAll));
}

function handleToolSelect(tool) {
  const { dispatch, getState } = store;
  const { selectedTool, activeImg, data } = getState().review;
  const { usecases } = getState().helpers;

  const useCaseId = data[activeImg]?.use_case;
  const useCaseType = usecases[useCaseId]?.type;

  if (useCaseType === 'CLASSIFICATION' && tool === 'create-box') {
    return;
  }

  if (tool === 'zoom' && selectedTool === 'zoom') {
    tool = 'zoom-out';
  }

  if (selectedTool === tool) {
    return;
  }

  dispatch(setSelectedTool(tool));
}

export function fetchHotKeyDefect(e) {
  if (e.repeat) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  const avoidableKeys = ['Shift', 'Alt', 'Meta', 'Control'];

  // newShortCutKey will always follow the this sequence Ctrl, Shift, Alt, Arrow, Alphabets
  const newShortCutKey = [];

  if (e.ctrlKey || e.metaKey) {
    newShortCutKey.push('ctrl');
  }
  if (e.shiftKey) {
    newShortCutKey.push('shift');
  }
  if (e.altKey) {
    newShortCutKey.push('alt');
  }
  if (e.key.includes('Arrow')) {
    newShortCutKey.push(e.key.split('Arrow')[1]);
  }
  if (e.key && !avoidableKeys.includes(e.key)) {
    newShortCutKey.push(e.key.toLowerCase());
  }

  return newShortCutKey.join('+').toUpperCase();
}

function handleImgKeyNavigation(e, annotationType, onLabelsChange) {
  // prevent hotkey execution when review state object ahs hot keys disabled like for popups
  const disableHotKeys = store.getState().review.disableHotKeys;
  if (disableHotKeys) {
    return;
  }

  // Prevent keydown event from being handled multiple times while held down the key or combo
  if (e.repeat) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  const newShortCutKey = fetchHotKeyDefect(e);

  if (DEFECT_HOT_KEYS[newShortCutKey]) {
    e.preventDefault();
    e.stopPropagation();
    const { getState } = store;
    const { activeImg, data } = getState().review;
    onLabelsChange?.({
      ...DEFECT_HOT_KEYS[newShortCutKey],
      ids: [
        ...(activeImg.map(
          i =>
            data[i][annotationType === CLUSTERING ? 'fileSetId' : 'wafer_dot']
        ) || []),
      ],
    });

    return handleUserClassificationChange(
      DEFECT_HOT_KEYS[newShortCutKey],
      annotationType
    );
  }

  if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
    e.preventDefault();
    e.stopPropagation();

    return handleToggleSelectAll();
  }

  if (!(e.ctrlKey || e.metaKey) && TOOL_KEYS[e.key.toLowerCase()]) {
    e.preventDefault();
    e.stopPropagation();

    return handleToolSelect(TOOL_KEYS[e.key.toLowerCase()]);
  }

  if (
    document.activeElement.tagName.toLowerCase() !== 'input' &&
    e.key.includes('Arrow') &&
    !(e.ctrlKey || e.shiftKey || e.metaKey)
  ) {
    e.preventDefault();
    e.stopPropagation();

    const key = e.key.split('Arrow')[1];

    switch (key) {
      case 'Down':
      case 'Right':
        handleImgClickNavigation('down', e);
        break;

      case 'Up':
      case 'Left':
        handleImgClickNavigation('up', e);
        break;

      default:
        return;
    }
  }
}

function calcCardIndex(rowIdx, colIdx, colCount) {
  return colCount * rowIdx + colIdx;
}

export function addImageModeParam(
  annotationType,
  activeImageMode,
  parsedParams
) {
  if (annotationType && activeImageMode) {
    const imageModeParam = getFileSetApiParamAnnotationType(
      annotationType,
      activeImageMode,
      false
    )
      ?.slice(1)
      .split('=');

    if (imageModeParam.length > 0) {
      parsedParams[imageModeParam[0]] = imageModeParam[1];
    }
  }
}

function handleAddFolderTags(tags, annotationType) {
  const { getState, dispatch } = store;
  const {
    activeImg,
    selectAll,
    data: fileSets,
    searchText,
    appliedForAllModelId,
    selectedWaferDots,
    selectedClusterRectangle,
    activeImageMode,
  } = getState().review;

  const parsedParams = getDateFromParams(
    getFileSetLocationParams(window.location.search),
    undefined,
    true
  );

  let encodedString = {};

  if (!selectAll) {
    encodedString = btoa(
      `id__in=${getFileSetIds(selectAll, activeImg, fileSets)}`
    );
  } else {
    delete parsedParams['allWafersId'];

    if (appliedForAllModelId) {
      parsedParams['ml_model_id__in'] = appliedForAllModelId;
    }

    if (searchText) {
      parsedParams['files__name__icontains'] = searchText;
    }

    if (selectedWaferDots?.length > 0 && annotationType !== CLUSTERING) {
      parsedParams['wafer_dot__in'] = selectedWaferDots.join(',');
    }

    if (selectedClusterRectangle && annotationType === CLUSTERING) {
      parsedParams['cluster_rectangle'] =
        selectedClusterRectangle.x1 +
        ',' +
        selectedClusterRectangle.y1 +
        ',' +
        selectedClusterRectangle.x2 +
        ',' +
        selectedClusterRectangle.y2;
    }

    addImageModeParam(annotationType, activeImageMode, parsedParams);

    encodedString = encodeURL(parsedParams);
  }

  api
    .updateTagsOnFilesets({
      tag_ids: tags.map(x => x.id),
      file_set_filters: encodedString,
    })
    .then(() => {
      infoToast('Image tag added', true, {
        toastId: 'toast-add-tags-to-images-success',
      });
      if (selectAll) {
        reloadReviewPage();
        return;
      }
      if (activeImg.length < 50) {
        api
          .getFilesetTags(getFileSetIds(selectAll, activeImg, fileSets))
          .then(res => {
            const data = formatFileSetData(res?.results || []);
            dispatch(
              updateFilsetTagsById(
                keyBy(
                  data?.map(x => ({ id: x.id, tags: x.tags })),
                  'id'
                )
              )
            );
            dispatch(setSelectAll(false));
          })
          .catch(() => {
            infoToast('Tags are not updated in UI please refresh the page.');
          });
      } else {
        infoToast('Please refresh to see updated tags.');
      }
    })
    .catch(() => {
      infoToast('Something went wrong.');
    });
}

function handleRemoveFolderTags(tags, isAllRemove = false, annotationType) {
  const { getState, dispatch } = store;
  const {
    activeImg,
    selectAll,
    data: fileSets,
    searchText,
    appliedForAllModelId,
    selectedWaferDots,
    selectedClusterRectangle,
    activeImageMode,
  } = getState().review;

  const parsedParams = getDateFromParams(
    getFileSetLocationParams(window.location.search),
    undefined,
    true
  );

  let encodedString = null;

  if (!selectAll) {
    encodedString = btoa(
      `id__in=${getFileSetIds(selectAll, activeImg, fileSets)}`
    );
  } else {
    delete parsedParams['allWafersId'];

    if (appliedForAllModelId) {
      parsedParams['ml_model_id__in'] = appliedForAllModelId;
    }

    if (searchText) {
      parsedParams['files__name__icontains'] = searchText;
    }

    if (selectedWaferDots?.length > 0 && annotationType !== CLUSTERING) {
      parsedParams['wafer_dot__in'] = selectedWaferDots.join(',');
    }

    if (selectedClusterRectangle && annotationType === CLUSTERING) {
      parsedParams['cluster_rectangle'] =
        selectedClusterRectangle.x1 +
        ',' +
        selectedClusterRectangle.y1 +
        ',' +
        selectedClusterRectangle.x2 +
        ',' +
        selectedClusterRectangle.y2;
    }

    addImageModeParam(annotationType, activeImageMode, parsedParams);

    encodedString = encodeURL(parsedParams);
  }

  api
    .removeTagsOnFilesets(
      isAllRemove
        ? { remove_all_tags: true, file_set_filters: encodedString }
        : { tag_ids: tags.map(x => x.id), file_set_filters: encodedString }
    )
    .then(() => {
      infoToast('Image tag removed', true, {
        toastId: 'toast-remove-tag-from-images-success',
      });
      if (selectAll) {
        reloadReviewPage();
        return;
      }
      if (activeImg.length < 50) {
        api
          .getFilesetTags(getFileSetIds(selectAll, activeImg, fileSets))
          .then(res => {
            const data = formatFileSetData(res?.results || []);
            dispatch(
              updateFilsetTagsById(
                keyBy(
                  data?.map(x => ({ id: x.id, tags: x.tags })),
                  'id'
                )
              )
            );
            dispatch(setSelectAll(false));
          })
          .catch(() => {
            infoToast('Tags are not updated in UI please refresh the page.');
          });
      } else {
        infoToast('Please refresh to see updated tags.');
      }
    })
    .catch(() => {
      infoToast('Something went wrong.');
    });
}

function loadImageList(ref, lastCursorCallRef, annotationType) {
  const { getState, dispatch } = store;
  const {
    fetchingReviewData,
    next: nextData,
    appliedForAllModelId,
    activeImageMode,
    useAIAssistance,
    data,
  } = getState().review;

  const isInViewPort = offset => {
    const lastLoadedChild = document.getElementById(
      `thumbnail-card-${data.length - (offset ?? 1)}`
    );

    if (!ref.current || !lastLoadedChild) {
      return false;
    }

    const parentRect = ref.current.getBoundingClientRect();
    const childRect = lastLoadedChild.getBoundingClientRect();

    return childRect.top <= parentRect.bottom;
  };

  if (
    (isInViewPort() || isInViewPort(20)) &&
    !fetchingReviewData &&
    !!nextData &&
    lastCursorCallRef.current !== nextData
  ) {
    dispatch(setFetchingReviewDataBackground(true));

    return axios
      .get(`${nextData}`)
      .then(({ data: res }) => {
        lastCursorCallRef.current = nextData;

        dispatch(setReviewDataNextApi(updateNextDataURL(res.next)));

        dispatch(
          setReviewData({
            data: res?.results || [],
            isNewData: false,
            annotationType,
            activeImageMode,
            modelId:
              appliedForAllModelId && useAIAssistance
                ? appliedForAllModelId
                : undefined,
          })
        );
      })
      .catch(() => {
        dispatch(setFetchingReviewDataBackground(false));
      });
  }

  return new Promise(resolve => {
    resolve();
  });
}

function getCurrentModelFromState() {
  const {
    appliedForAllModelId,
    fileSetDefects = {},
    data = [],
    activeImg = [],
    useAIAssistance,
  } = store.getState().review || {};
  const activeIndex = activeImg?.[0];
  const activeRow = activeIndex !== undefined ? data?.[activeIndex] : undefined;
  // fileSetDefects is keyed by file id (data[].id); fileSetId is only a fallback.
  const activeDefects =
    fileSetDefects?.[activeRow?.id] ?? fileSetDefects?.[activeRow?.fileSetId];
  const { model_classifications = [], model_detections = [] } =
    activeDefects || {};

  let modelId = null;

  if (!useAIAssistance) {
    return null;
  }

  if (appliedForAllModelId) {
    modelId = appliedForAllModelId;
  } else if (model_classifications.length) {
    modelId = model_classifications[0]?.ml_model;
  } else if (model_detections.length) {
    modelId =
      model_detections[0]?.ml_model?.id ??
      model_detections[0]?.ml_model ??
      model_classifications[0]?.ml_model?.id;
  }

  return modelId;
}

function getSortingParams() {
  const searchParams = new URLSearchParams(window.location.search);
  let sorting = searchParams.get('sorting');

  if (sorting) {
    sorting = queryString.parse(sorting);
  } else {
    sorting = {
      sortDirection: 'ascending',
      sortBy: 'created_ts',
    };
  }

  if (!sorting?.sortBy) {
    return '';
  }

  let params = `&ordering=${sorting.sortDirection === 'ascending' ? '' : '-'}${
    sorting?.sortBy
  }`;

  if (
    sorting.sortBy === SORTING_CONSTANTS.SIMILARITY ||
    sorting.sortBy === SORTING_CONSTANTS.AI_OUTPUT
  ) {
    const modelId = getCurrentModelFromState();
    params = params + `&ordering_ml_model=${modelId}`;
  }

  return params;
}

function isModelExist(id) {
  const { modelsDict } = store.getState().review;

  if (modelsDict[id]) {
    return true;
  }
  return false;
}

function createRegionPayload(region) {
  const { type, x, y, w, h, tags } = region;
  const tempObj = {};

  tempObj.region = {
    type,
    coordinates: {
      x,
      y,
      w,
      h,
    },
  };

  tempObj.defects = [...new Set(tags?.map(tag => tag.id) ?? [])];

  return tempObj;
}

function moveFocusToParent() {
  document.elementFromPoint(0, 0).closest('div').focus();
  return;
}

const handleOtherDefectChange = async (defect, annotationType) => {
  const { getState, dispatch } = store;
  const { data, activeImg, fileSetDefects, activeImageMode } =
    getState().review;

  const fileSet = data[activeImg[0]];
  const defectIds = [];

  if (!fileSet) {
    return;
  }

  const showWarningOnLabelChange =
    (
      getAIDefects(
        fileSetDefects[data[activeImg[0]].id],
        getState().helpers.usecases[fileSet.use_case]?.type
      ) || []
    ).some(d => !!d.is_killer) &&
    defect.id &&
    !defect.is_killer &&
    import.meta.env
      .VITE_APP_SHOW_WARNING_FOR_AI_KILLER_GT_NON_KILLER_SELECTION === 'true';

  if (Array.isArray(defect)) {
    defect.forEach(item => defectIds.push(item.id));
  } else if (defect.id) {
    defectIds.push(defect.id);
  }

  if ([MANUAL_CLASSIFY, AUDIT].includes(annotationType)) {
    if (isFileSetUpdatePermission(fileSet.id)) {
      if (defectIds.length > 0) {
        dispatch(
          setFileSetLocalDefects({
            id: fileSet.id,
            defect: Array.isArray(defect) ? defect[0] : defect,
          })
        );

        if (
          activeImageMode.toLowerCase() === 'unclassified' ||
          activeImageMode.toLowerCase() === 'unaudited'
        ) {
          addActiveImgToTransferableImageIndexes();
        } else if (
          activeImageMode.toLowerCase() === 'classified' ||
          activeImageMode.toLowerCase() === 'audited'
        ) {
          removeAcitveImgFromTransferableImageIndexes();
        }
      } else {
        dispatch(setFileSetLocalDefects({ id: fileSet.id, defect: {} }));

        if (
          activeImageMode.toLowerCase() === 'unclassified' ||
          activeImageMode.toLowerCase() === 'unaudited'
        ) {
          removeAcitveImgFromTransferableImageIndexes();
        } else if (
          activeImageMode.toLowerCase() === 'classified' ||
          activeImageMode.toLowerCase().includes('audited')
        ) {
          addActiveImgToTransferableImageIndexes();
        }
      }
      autoUpdateDefectsInBackground(annotationType);
    }
    showWarningOnLabelChange && warningToast('Label might be incorrect.');
    return;
  }

  const { userInfo } = getState().common;

  const modelId = getCurrentModelFromState();

  const shouldUpdate = fileSetDefects[fileSet.id]['gt_classifications'];

  if (shouldUpdate) {
    const gtDefectId = fileSetDefects[fileSet.id]['gt_classifications']['id'];

    await dispatch(
      updateUserClassification({
        gtDefectId,
        defectIds,
        fileSet,
        userInfo: userInfo.id,
        modelId,
        annotationType,
      })
    );
  } else {
    await dispatch(
      addUserClassification({
        fileSet,
        defects: defectIds,
        user: userInfo.id,
        modelId: modelId,
        annotationType,
      })
    );
  }

  dispatch(getUserClassification(fileSet.id));
  !showWarningOnLabelChange
    ? infoToast('1 image is updated successfully.', false, {
        toastId: 'toast-image-updated-success',
      })
    : warningToast('Label might be incorrect.', false);
};

const getDictinctModelFromFileDefect = fileSetDefects => {
  return [
    ...new Set(
      Object.values(fileSetDefects)
        .map(item => {
          const { model_classifications, model_detections } = item;
          if (model_classifications) {
            return model_classifications.map(model => model.ml_model);
          } else if (model_detections) {
            return model_detections.map(model => model.ml_model);
          }
          return [];
        })
        .flat()
    ),
  ];
};

function setContainerLayout(ref, itemCount) {
  return new Promise((resolve, reject) => {
    const { getState, dispatch } = store;
    const { data, fetchingReviewData } = getState().review;

    if (!itemCount) {
      dispatch(setContainerMeta(null));
    } else if (itemCount && data.length) {
      const { offsetWidth, offsetTop } = ref.current;
      const maxHeight = localStorage.getItem(REVIEW_CONTAINER_META_MAX_HEIGHT);
      let idx = 0;

      const img = new Image();

      img.onload = () => {
        const meta = calcContainerLayout({
          originalImgHeight: img.height,
          originalImgWidth: img.width,
          totalItemCount: itemCount,
          totalWidth: offsetWidth,
          maxHeight: Number(maxHeight) || undefined,
        });

        dispatch(
          setContainerMeta({
            ...meta,
            containerHeight:
              window.innerHeight - offsetTop - (fetchingReviewData ? 30 : 12),
            totalWidth: offsetWidth,
            originalImgHeight: img.height,
            originalImgWidth: img.width,
            totalItemCount: itemCount,
          })
        );

        resolve();
      };

      img.onerror = () => {
        if (idx <= Math.min(data.length - 1, 4)) {
          idx += 1;
          img.src = data[idx].src;
        } else {
          reject(new Error('Failed to load image. Please try again.'));
        }
      };

      img.src = data[idx].src;
    }
  });
}

const reloadReviewPage = () => {
  window.location.reload();
};

const isSameFilteredLocation = url => {
  const { pathname: windowPath, search: windowSearchQuery } = window.location;
  const [urlPathName, urlSearchQuery] = url.split('?');

  if (windowPath === urlPathName) {
    if (windowSearchQuery.substring(1) === urlSearchQuery) {
      return true;
    }
    return false;
  }

  return false;
};

function resetTransferableImageIndexes() {
  const { dispatch } = store;

  dispatch(updateTransferableImageIndexes(new Set()));
}

/**
 * @param {string} [annotationType]
 * @param {Set<any> | null} [targetIndexes]
 */
const reArrangeManualAuditFiles = (
  annotationType = MANUAL_CLASSIFY,
  targetIndexes = null
) => {
  const { getState, dispatch } = store;

  const {
    transferableImageIndexes: stateTransferableIndexes,
    data: fileSets,
    activeImg,
    activeImageMode,
  } = getState().review;

  const transferableImageIndexes = targetIndexes ?? stateTransferableIndexes;

  if (!transferableImageIndexes || transferableImageIndexes.size === 0) {
    return;
  }

  const fileIds = [];

  for (const x of transferableImageIndexes) {
    if (fileSets[x]?.id) {
      fileIds.push(fileSets[x].id);
    }
  }

  if (fileIds.length === 0) {
    // Nothing is moved out, so clear the set without advancing the selection.
    dispatch(updateTransferImageIndexes(new Set()));
    return;
  }

  const remainingFilesCount = Math.max(0, fileSets.length - fileIds.length);
  const updatedActiveImg = activeImg.map(idx => {
    let transferedBeforeOrAt = 0;
    for (let i = 0; i <= idx; i++) {
      if (transferableImageIndexes.has(i)) {
        transferedBeforeOrAt++;
      }
    }
    const newIdx = idx - transferedBeforeOrAt;
    if (remainingFilesCount === 0) {
      return 0;
    }
    return Math.max(0, Math.min(remainingFilesCount - 1, newIdx));
  });

  const finalActiveImg = updatedActiveImg.length > 0 ? updatedActiveImg : [0];

  dispatch(removeFileSet(fileIds));
  dispatch(updateImageModes(fileIds));
  resetTransferableImageIndexes();

  dispatch(setActiveImg(finalActiveImg));

  const type = getImageClassificationType(activeImageMode);

  infoToast(
    `${
      fileIds.length > 1
        ? `${fileIds.length} images are`
        : `${fileIds.length} image is`
    } ${annotationType === MANUAL_CLASSIFY ? type : 'Audited'} successfully.`
  );
};

export function removeAcitveImgFromTransferableImageIndexes() {
  const { getState, dispatch } = store;
  const { transferableImageIndexes, activeImg } = getState().review;

  const updated = new Set(transferableImageIndexes);
  for (const x of activeImg) {
    updated.delete(x);
  }

  dispatch(updateTransferableImageIndexes(updated));
}

export function addActiveImgToTransferableImageIndexes() {
  const { getState, dispatch } = store;
  const { transferableImageIndexes, activeImg } = getState().review;

  dispatch(
    updateTransferableImageIndexes(
      new Set([...transferableImageIndexes, ...activeImg])
    )
  );
}

export function isAutoModel(fileSetId) {
  const { getState } = store;
  const { fileSetDefects, modelsDict } = getState().review;

  return fileSetDefects[fileSetId]['model_classifications']?.some(d => {
    return d.classification_defects.some(
      e =>
        Number(e.confidence) >=
        Number(modelsDict[d.ml_model]?.confidence_threshold)
    );
  });
}

function isAllowUnload() {
  const { getState } = store;
  const { fileSetLocalDefects } = getState().review;

  return Object.values(fileSetLocalDefects).length > 0;
}

export function handleBeforeUnload(event) {
  if (isAllowUnload()) {
    event.preventDefault();
    event.returnValue = 'Unsaved data. Are you sure?';
    return 'Unsaved data. Are you sure?';
  }
}

export async function handleBeforeBack() {
  const { getState } = store;
  const { review } = getState();
  const { fileSetLocalDefects, fileSetLocalDefectsSavingStatus } = review;
  const entries = Object.entries(fileSetLocalDefects);

  if (
    entries.length > 0 &&
    fileSetLocalDefectsSavingStatus !== fileSetLocalDefectsStatus.LOADING
  ) {
    window.history.forward();
    await bulkUpdateFilesetDefects();
    window.history.back();
  }
}

const DEFECT_FETCH_CHUNK_SIZE = 200;

const waitForPaint = () =>
  new Promise(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });

const hasModelData = defects =>
  !!defects?.model_classifications?.some(
    item => item?.classification_defects?.length > 0
  ) || !!defects?.model_detections?.length;

// After the post-save refetch, re-applies (as a SET_REVIEW_DEFECTS payload, so
// the reducer merges it over the server data):
//  - the labels that were just saved, because the API can still return the
//    pre-save labels right after a save;
//  - model classifications/detections the refetch wiped for any returned file.
//    The table decides whether to show the AI Prediction / Similarity Score
//    columns by sampling the first fileSetDefects entries, so entries must not
//    lose model data they already had.
const buildPostRefetchPatch = ({
  entries,
  responses = [],
  previous = {},
  current = {},
}) => {
  const savedLabels = new Map(
    entries.map(([id, defect]) => [String(id), defect])
  );
  const affectedIds = new Set(savedLabels.keys());
  responses.forEach(response =>
    Object.values(response || {}).forEach(item =>
      item?.files?.forEach(file => affectedIds.add(String(file?.id)))
    )
  );

  const files = [];
  affectedIds.forEach(id => {
    const latest = current[id];
    if (!latest) {
      return;
    }

    const restoreModelData =
      hasModelData(previous[id]) && !hasModelData(latest);
    const hasSavedLabel = savedLabels.has(id);
    if (!restoreModelData && !hasSavedLabel) {
      return;
    }

    const file = { ...latest };
    if (restoreModelData) {
      file.model_classifications = previous[id].model_classifications;
      file.model_detections = previous[id].model_detections;
    }
    if (hasSavedLabel) {
      const defect = savedLabels.get(id);
      const gtClassifications = latest.gt_classifications || {};
      file.gt_classifications = {
        ...gtClassifications,
        classification_defects:
          defect && Object.keys(defect).length > 0
            ? [
                {
                  ...(gtClassifications.classification_defects?.[0] || {}),
                  defect,
                },
              ]
            : [],
      };
    }
    files.push(file);
  });

  return files.length > 0 ? { postRefetch: { files } } : null;
};

export async function bulkUpdateFilesetDefects(
  overrideDefects = null,
  noPopup = false,
  annotationType = MANUAL_CLASSIFY,
  fromAutoUpdate = false
) {
  const { getState, dispatch } = store;
  const { review = {}, common = {} } = getState() || {};
  const {
    fileSetLocalDefects: stateDefects = {},
    fileSetLocalDefectsSavingStatus,
    data = [],
  } = review;
  const fileSetLocalDefects = overrideDefects ?? stateDefects;
  const { userInfo = {} } = common;
  const entries = Object.entries(fileSetLocalDefects);

  try {
    if (
      entries.length > 0 &&
      fileSetLocalDefectsSavingStatus !== fileSetLocalDefectsStatus.LOADING
    ) {
      if (!noPopup) {
        dispatch(
          setFileSetLocalDefectsSavingStatus(fileSetLocalDefectsStatus.LOADING)
        );
        // Let the browser paint the "Labels are being saved" popup before the
        // heavy payload build / request for thousands of entries starts.
        await waitForPaint();
      }

      const payload = {
        file_defects: entries.reduce((acc, curr) => {
          acc[curr[0]] = Object.values(curr[1]).length > 0 ? [curr[1].id] : [];
          return acc;
        }, {}),
        userId: userInfo.id,
      };

      await api.addBulkClassification(payload);

      const currentReview = getState()?.review || {};
      const currentLocalDefects = currentReview.fileSetLocalDefects || {};
      const remainingDefects = { ...currentLocalDefects };
      entries.forEach(([id]) => {
        delete remainingDefects[id];
      });
      dispatch(resetFileSetLocalDefects());
      if (Object.keys(remainingDefects).length > 0) {
        dispatch(setBulkFileSetLocalDefects(remainingDefects));
      }

      const { transferableImageIndexes = new Set() } = currentReview;
      const updatedTransferableIndexes = new Set(transferableImageIndexes);
      const currentData = currentReview.data || data || [];
      const updatedEntryIds = new Set(entries.map(([id]) => String(id)));

      currentData.forEach((item, index) => {
        const fileSetId = item?.fileSetId;
        const id = item?.id;
        if (
          (fileSetId && updatedEntryIds.has(String(fileSetId))) ||
          (id && updatedEntryIds.has(String(id)))
        ) {
          updatedTransferableIndexes.delete(index);
        }
      });
      // Only prune saved rows from the set. updateTransferableImageIndexes also
      // advances activeImg (label-and-move-next), which made the selection
      // jump one row ahead on every auto-save.
      dispatch(updateTransferImageIndexes(updatedTransferableIndexes));

      // Local defects are keyed by file id (data[].id) but getDefect filters by
      // file-set id (data[].fileSetId). Fetching with file ids returns unrelated
      // file sets, so the saved rows never refresh and the merged-in entries can
      // hide the AI columns.
      const fileSetIdByFileId = new Map(
        currentData.map(item => [String(item?.id), item?.fileSetId])
      );
      const entryIds = [
        ...new Set(
          entries.map(([fileId]) =>
            String(fileSetIdByFileId.get(String(fileId)) ?? fileId)
          )
        ),
      ];

      const modelId = getCurrentModelFromState();

      // The saving popup stays up (status resets only after the refetch below).
      await new Promise(resolve =>
        setTimeout(resolve, DEFECT_REFETCH_DELAY_MS)
      );

      // ids go into the GET query string; a single request with thousands of
      // ids exceeds URL limits and fails silently, leaving labels blank.
      // Snapshot before the fetches merge new data into fileSetDefects.
      const previousDefects = getState()?.review?.fileSetDefects;
      const defectFetches = [];
      for (let i = 0; i < entryIds.length; i += DEFECT_FETCH_CHUNK_SIZE) {
        defectFetches.push(
          dispatch(
            getDefect(entryIds.slice(i, i + DEFECT_FETCH_CHUNK_SIZE), modelId)
          )
        );
      }
      const responses = await Promise.all(defectFetches);
      // Applied after the fetch, never before, so a stale response can't win.
      const postRefetchPatch = buildPostRefetchPatch({
        entries,
        responses,
        previous: previousDefects,
        current: getState()?.review?.fileSetDefects,
      });
      if (postRefetchPatch) {
        dispatch(setReviewDefects(postRefetchPatch));
      }

      if (!noPopup) {
        dispatch(
          setFileSetLocalDefectsSavingStatus(fileSetLocalDefectsStatus.NONE)
        );
      }

      if (
        !fromAutoUpdate &&
        Object.keys(remainingDefects).length >=
          GLOBAL_CONSTS.BULK_CREATE_SYNC_LIMIT &&
        import.meta.env.VITE_APP_AUTO_UPDATE_DEFECT_LABEL_AT_SYNC_LIMIT ===
          'true'
      ) {
        autoUpdateDefectsInBackground(annotationType);
      }

      globalQueryClient.invalidateQueries({ queryKey: ['fileSets'] });
    }
  } catch (err) {
    console.error(err);
    dispatch(
      setFileSetLocalDefectsSavingStatus(fileSetLocalDefectsStatus.ERROR)
    );
    throw err;
  }
}

export function isFilesetLabelUpdatedByAdmin(fileSetId) {
  const { getState } = store;
  const { fileSetDefects } = getState().review;

  return !!fileSetDefects[fileSetId]?.['gt_classifications']
    ?.is_created_by_admin;
}

export function isFileSetUpdatePermission(fileSetId) {
  const { getState } = store;
  const { userInfo } = getState().common;

  return !!userInfo?.is_staff || !isFilesetLabelUpdatedByAdmin(fileSetId);
}

/**
 * Executes a callback (or array of callbacks) capturing the current transferableImageIndexes
 * as a snapshot before any async operations start, followed by rearranging the filesets.
 *
 * @param {string} [annotationType]
 * @param {Function | Function[]} bulkUpdateCallback - Function or array of functions.
 * Callbacks receive the index snapshot as an argument.
 * @param {...Function} additionalPipeFns
 */
const executeWithTransferableImageSnapshot = (
  annotationType = MANUAL_CLASSIFY,
  bulkUpdateCallback,
  ...additionalPipeFns
) => {
  const { transferableImageIndexes } = store.getState().review;
  const indexesToTransfer = new Set(transferableImageIndexes);
  const preRearrangeFns = Array.isArray(bulkUpdateCallback)
    ? bulkUpdateCallback.map(fn => () => fn(indexesToTransfer))
    : [() => bulkUpdateCallback(indexesToTransfer)];

  return pipeFunc(
    ...preRearrangeFns,
    () => reArrangeManualAuditFiles(annotationType, indexesToTransfer),
    ...additionalPipeFns
  );
};

// Mirrors the wafer-map step of the manual "Move" popups; must run before
// reArrangeManualAuditFiles removes the file sets the indexes point to.
const updateWaferMapDotStatus = indexesToTransfer => {
  const annotationRef = getSharedRef('sharedAnnotationRef');
  if (!annotationRef?.current) {
    return;
  }
  const { data: fileSets, activeImageMode } = store.getState().review;
  const imgMode = activeImageMode?.toLowerCase();

  annotationRef.current
    .updateWaferDotStatus?.({
      ids: Array.from(indexesToTransfer).map(id => fileSets[id]?.wafer_dot),
      status:
        imgMode === 'unclassified' || imgMode === 'unaudited'
          ? 'classified'
          : 'unclassified',
    })
    ?.catch(console.error);
};

const autoUpdateDefectsInBackground = (annotationType = MANUAL_CLASSIFY) => {
  const { BULK_CREATE_SYNC_LIMIT } = GLOBAL_CONSTS;
  const { getState } = store;
  const { review = {} } = getState() || {};
  const { fileSetLocalDefects = {}, fileSetLocalDefectsSavingStatus } = review;
  const entries = Object.entries(fileSetLocalDefects);

  if (
    entries.length >= BULK_CREATE_SYNC_LIMIT &&
    fileSetLocalDefectsSavingStatus !== fileSetLocalDefectsStatus.LOADING &&
    import.meta.env.VITE_APP_AUTO_UPDATE_DEFECT_LABEL_AT_SYNC_LIMIT === 'true'
  ) {
    executeWithTransferableImageSnapshot(
      annotationType,
      [
        // noPopup=false on purpose: the LOADING popup/status is what blocks a
        // second auto-update from starting while this save is in flight.
        // fromAutoUpdate=true: don't re-trigger from inside this chain; a nested
        // chain would rearrange file sets using index snapshots that this
        // chain's own rearrange step has already made stale.
        () => bulkUpdateFilesetDefects(null, false, annotationType, true),
        indexesToTransfer => updateWaferMapDotStatus(indexesToTransfer),
      ],
      () =>
        getSharedRef('sharedManualReclassV2HistogramRef')?.current?.refetch?.(),
      () => {
        globalQueryClient.invalidateQueries({ queryKey: ['fileSets'] });
      }
    );
  }
};

export {
  autoUpdateDefectsInBackground,
  calcCardIndex,
  calcContainerLayout,
  createRegionPayload,
  executeWithTransferableImageSnapshot,
  getCurrentModelFromState,
  getDictinctModelFromFileDefect,
  getSortingParams,
  handleAddFolderTags,
  handleCardClick,
  handleCardDoubleClick,
  handleCardSelect,
  handleImgClickNavigation,
  handleImgKeyNavigation,
  handleOtherDefectChange,
  handleRemoveFolderTags,
  isModelExist,
  isSameFilteredLocation,
  loadImageList,
  moveFocusToParent,
  reArrangeManualAuditFiles,
  reloadReviewPage,
  resetTransferableImageIndexes,
  setContainerLayout,
};
